import { ordersLexicon } from "./lexicon";
import { digitsToWords, numberToWords, spellLetters } from "./numbers";
import type {
  CriticalKind,
  NormalizeSpeechOptions,
  SpeechNormalization,
  UnresolvedTokenLogEntry,
  UnresolvedTokenOccurrence,
} from "./types";

const PHONETIC: Readonly<Record<string, string>> = Object.freeze({
  A: "alpha",
  B: "bravo",
  C: "charlie",
  D: "delta",
  E: "echo",
  F: "foxtrot",
  G: "golf",
  H: "hotel",
  I: "india",
  J: "juliett",
  K: "kilo",
  L: "lima",
  M: "mike",
  N: "november",
  O: "oscar",
  P: "papa",
  Q: "quebec",
  R: "romeo",
  S: "sierra",
  T: "tango",
  U: "uniform",
  V: "victor",
  W: "whiskey",
  X: "x-ray",
  Y: "yankee",
  Z: "zulu",
});

const PURE_EQUIPMENT = new Set(["BTR", "BRDM", "BRM", "MT-LB", "BMP", "PKM"]);

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function tokenRegex(token: string): RegExp {
  const escaped = escapeRegex(token);
  return new RegExp(`(?<![A-Za-z0-9_])${escaped}(?![A-Za-z0-9_])`, "g");
}

export function normalizeEquipmentDesignator(value: string): string | null {
  const numbered = /^([A-Z]+)-(\d+)([A-Z]?)$/.exec(value);
  if (numbered !== null) {
    /* v8 ignore next -- these captures are mandatory in the guarding regex */
    const letters = numbered[1] ?? "";
    /* v8 ignore next -- these captures are mandatory in the guarding regex */
    const number = numbered[2] ?? "";
    /* v8 ignore next -- the optional capture returns an empty string */
    const trailingLetter = numbered[3] ?? "";
    const trailing = trailingLetter === "" ? "" : ` ${trailingLetter}`;
    return `${spellLetters(letters)} ${numberToWords(Number(number))}${trailing}`;
  }
  if (!PURE_EQUIPMENT.has(value)) return null;
  return spellLetters(value);
}

function equipment(text: string): string {
  return text.replace(
    /\b(?:[A-Z]{1,6}-\d+[A-Z]?|BTR|BRDM|BRM|MT-LB|BMP|PKM)\b/g,
    (token) => {
      const spoken = normalizeEquipmentDesignator(token);
      /* v8 ignore next -- the regex accepts only forms handled above */
      return spoken ?? token;
    },
  );
}

/**
 * Suppresses a grid zone designator from the spoken stream.
 *
 * Ruling of 12 September 2026. A designator immediately before a grid label
 * speaks as three words in the same shape doc 03 section 5 gives callsigns,
 * at the moment the trainee is writing down a grid. It carries nothing he
 * needs: a squadron operating inside one map sheet never uses it, and it is
 * map-administrative rather than tactical.
 *
 * Only the spoken stream is affected. The display pane stays source-faithful.
 * The designator is recognised only where it sits directly before a grid
 * label, so a callsign of the same shape is never touched.
 */
function gridZoneDesignators(text: string): string {
  return text.replace(/\b\d{1,2}[A-HJ-NP-Z]\s+(?=(?:GR|grid)\b)/gi, "");
}

function grids(text: string): string {
  return text.replace(
    /\b(?:GR|grid)\s*[:#-]?\s+(\d+(?:\s+\d+)*)\b/gi,
    (_match, groups: string) => {
      const spokenGroups = groups.split(/\s+/).map(digitsToWords);
      return `grid, ${spokenGroups.join(", ")}`;
    },
  );
}

function callsigns(text: string): string {
  return text.replace(
    /\b(\d{2})([A-Z]?)\b/g,
    (_match, digits: string, letter: string) => {
      /* v8 ignore next -- the regex restricts letters to the complete A-Z map */
      const trailing = letter === "" ? "" : ` ${PHONETIC[letter] ?? letter}`;
      return `${digitsToWords(digits)}${trailing}`;
    },
  );
}

function unitDesignators(text: string): string {
  return text.replace(
    /\b(\d+)\s+(PPCLI|RCR|CMBG|BTG|Armd)\b/g,
    (_match, digits: string, unit: string) => {
      const spokenUnit = unit === "Armd" ? "armoured" : spellLetters(unit);
      return `${digitsToWords(digits)} ${spokenUnit}`;
    },
  );
}

function phasesDurationsAndBearings(text: string): string {
  return text
    .replace(
      /\bPh\s+(\d+)\b/g,
      (_match, number: string) => `phase ${numberToWords(Number(number))}`,
    )
    .replace(
      /\b(\d{4})\s+hrs\b/gi,
      (_match, digits: string) => `${digitsToWords(digits)} hours`,
    )
    .replace(
      /\b(\d+)\s*(hrs?|hours?|mins?|minutes?)\b/gi,
      (_match, number: string, unit: string) => {
        const normalizedUnit = /^h/i.test(unit) ? "hours" : "minutes";
        return `${numberToWords(Number(number))} ${normalizedUnit}`;
      },
    )
    .replace(
      /\b(\d{1,4})\s+mils\b/gi,
      (_match, digits: string) => `${digitsToWords(digits)} mils`,
    );
}

function applyLexicon(text: string): string {
  const entries = [...ordersLexicon.entries].sort(
    (left, right) => right.written.length - left.written.length,
  );
  return entries.reduce(
    (current, entry) =>
      current.replace(tokenRegex(entry.written), entry.spoken),
    text,
  );
}

function speakUnresolved(token: string): string {
  if (/^[A-Z]+-\d+[A-Z]?$/.test(token)) {
    const spoken = normalizeEquipmentDesignator(token);
    /* v8 ignore next -- the guarding regex is the equipment parser's grammar */
    return spoken ?? token;
  }
  if (/[a-z]/.test(token)) {
    return token;
  }
  return token
    .split(" ")
    .map((part) =>
      Array.from(part)
        .map((character) =>
          /\d/.test(character) ? digitsToWords(character) : character,
        )
        .join(" "),
    )
    .join(" ");
}

function unresolved(
  text: string,
  nodeId: string,
): {
  readonly text: string;
  readonly occurrences: readonly UnresolvedTokenOccurrence[];
  readonly logEntries: readonly UnresolvedTokenLogEntry[];
} {
  const occurrences: UnresolvedTokenOccurrence[] = [];
  const alternatives = [...ordersLexicon.unresolvedTokens]
    .sort((left, right) => right.length - left.length)
    .map(escapeRegex)
    .join("|");
  const pattern = new RegExp(
    `(?<![A-Za-z0-9_])(?:${alternatives}|[A-Z]{1,6}-[0-9]+[A-Z]?)(?![A-Za-z0-9_])`,
    "g",
  );
  const current = text.replace(pattern, (token) => {
    const equipmentMatch = /^([A-Z]+)-\d+[A-Z]?$/.exec(token);
    if (
      equipmentMatch?.[1] !== undefined &&
      ordersLexicon.equipmentDesignators.includes(equipmentMatch[1])
    ) {
      return token;
    }
    const spokenText = speakUnresolved(token);
    occurrences.push({
      id: `${nodeId}/unresolved-${String(occurrences.length + 1).padStart(2, "0")}`,
      nodeId,
      token,
      spokenText,
      blocksPlayback: false,
    });
    return spokenText;
  });
  return {
    text: current,
    occurrences,
    logEntries: occurrences.map(({ nodeId: occurrenceNodeId, token }) => ({
      schemaVersion: 1,
      action: "unresolved-token",
      nodeId: occurrenceNodeId,
      token,
    })),
  };
}

function criticalKinds(displayText: string): readonly CriticalKind[] {
  const kinds: CriticalKind[] = [];
  if (/\b(?:GR|grid)\b/i.test(displayText)) kinds.push("grid");
  if (/\b\d{2}[A-Z]\b|chain of command|\bCoC\b/i.test(displayText)) {
    kinds.push("callsign");
  }
  if (/\b(?:IFF|CoC|priority of targets?)\b/i.test(displayText)) {
    kinds.push("coordination");
  }
  if (/\b(?:MHz|kHz|frequency)\b/i.test(displayText)) kinds.push("frequency");
  if (/\b(?:code ?word|password)\b/i.test(displayText)) kinds.push("code-word");
  return kinds;
}

export function normalizeSpeechText(
  displayText: string,
  options: NormalizeSpeechOptions = {},
): SpeechNormalization {
  const nodeId = options.nodeId ?? "text";
  const unresolvedResult = unresolved(displayText, nodeId);
  const spokenText = applyLexicon(
    phasesDurationsAndBearings(
      unitDesignators(
        callsigns(grids(gridZoneDesignators(equipment(unresolvedResult.text)))),
      ),
    ),
  );
  return {
    displayText,
    spokenText,
    unresolvedTokens: unresolvedResult.occurrences,
    logEntries: unresolvedResult.logEntries,
    criticalKinds: criticalKinds(displayText),
  };
}
